import { useMemo } from "react";
import CustomModal from "@/components/modal/CustomModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { Pen, Trash2Icon } from "lucide-react";
import type { ShiftType } from "@utils/grade-util";
import type { GradeShiftGridRowDisplay } from "./GradeShiftConfigModal";
import type { TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import { Input } from "@/components/ui/input";
import ShiftIcon from "@/components/ui/shift-icon";
import ShiftCode from "@/components/shift/ShiftCode";
import { sortTeamsByName } from "@/utils/team-sort-util";

export type TeamShiftCounts = Partial<Record<ShiftType, number>>;

type TeamShiftConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  teams: TeamsSchema;
  memberCountByTeam: Record<number, number>;
  shiftSettings: Record<number, TeamShiftCounts>;
  shiftDisplayInfo: GradeShiftGridRowDisplay[];
  totalStaffCount: number;
  teamConfiguredTotal: number;
  unassignedCount?: number;
  isInvalidTeam: (teamId: number) => boolean;
  onMinStaffChange: (teamId: number, shift: ShiftType, value: string) => void;
  onRenameTeam?: (teamId: number, currentName: string) => void;
  onDeleteTeam?: (teamId: number) => void;
  onAddTeam?: () => void;
};

const TEXT = {
  close: "닫기",
  save: "저장",
  title: "Shift 인원 설정",
  totalStaff: "전체인원",
  configured: "Team 총 설정 인원",
  unassigned: "미지정",
  person: "명",
  addTeam: "+ Team 추가",
  rename: "이름 수정",
  remove: "삭제",
};

export default function TeamShiftConfigModal({
  isOpen,
  onClose,
  onConfirm,
  teams,
  memberCountByTeam,
  shiftSettings,
  shiftDisplayInfo,
  totalStaffCount,
  teamConfiguredTotal,
  unassignedCount = 0,
  isInvalidTeam,
  onMinStaffChange,
  onRenameTeam,
  onDeleteTeam,
  onAddTeam,
}: TeamShiftConfigModalProps) {
  const displayTeams = useMemo(
    () => sortTeamsByName(teams),
    [teams]
  );

  const colMinPx =
    displayTeams.length <= 5
      ? 108
      : Math.max(92, 108 - (displayTeams.length - 5) * 4);

  if (!isOpen) return null;

  return (
    <CustomModal.Panel
      onClose={onClose}
      className="w-fit max-w-[96vw] min-w-[550px] bg-background px-5"
      button
      btnCancelText={TEXT.close}
      btnSuccessText={TEXT.save}
      btnCancelOnClick={onClose}
      btnSuccessOnClick={onConfirm}
    >
      <div>
        <h2 className="mb-3 text-center font-pretendard text-2xl font-semibold text-sig">
          {TEXT.title}
        </h2>
      </div>

      <div className="mb-10 flex items-center justify-between gap-5 border-b-2 pb-3">
        <div className="flex gap-1 text-center font-pretendard text-sm text-muted-foreground">
          <div>
            {TEXT.totalStaff} {totalStaffCount}
            {TEXT.person}
          </div>
          <span className="opacity-50">/</span>
          <div>
            {TEXT.configured}{" "}
            <span className="font-semibold text-sig">
              {teamConfiguredTotal}
            </span>
            {TEXT.person}
          </div>
          <span className="opacity-50">/</span>
          <div>
            {TEXT.unassigned} {unassignedCount}
            {TEXT.person}
          </div>
        </div>
        <Button
          type="button"
          className="ml-auto"
          variant="outline"
          disabled={!onAddTeam}
          onClick={() => onAddTeam?.()}
        >
          {TEXT.addTeam}
        </Button>
      </div>

      <div className="mb-5 flex flex-col">
        <div className="flex items-start justify-center">
          <div
            className="min-w-0"
            style={{ width: `${36 + displayTeams.length * colMinPx}px` }}
          >
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col style={{ width: 36 }} />
                {displayTeams.map((team) => (
                  <col key={team.team_id} style={{ width: colMinPx }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="bg-background p-0" />
                  {displayTeams.map((team, idx) => {
                    const isError = isInvalidTeam(team.team_id);
                    const teamMemberCount =
                      memberCountByTeam[team.team_id] ?? 0;

                    return (
                      <th
                        key={team.team_id}
                        scope="col"
                        className={cn(
                          "min-w-0 overflow-visible text-center align-middle font-normal transition-colors",
                          isError && "text-destructive"
                        )}
                      >
                        <div
                          className={cn(
                            "group relative w-full min-w-0 translate-x-[-.5px] rounded-t-lg border-3 border-border bg-muted/40 px-2 py-2 hover:bg-muted",
                            idx === displayTeams.length - 1
                              ? "border-r-3"
                              : "border-r-0"
                          )}
                        >
                          <div className="pointer-events-none absolute left-1/2 top-0 z-10 flex -translate-x-1/2 gap-[3px] p-2 opacity-0 transition-all group-hover:pointer-events-auto group-hover:top-[-40px] group-hover:opacity-100">
                            <button
                              type="button"
                              className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full border border-muted-foreground/50 text-muted-foreground/50 transition-colors hover:border-muted-foreground/80 hover:text-muted-foreground/80"
                              aria-label={`${team.team_name} ${TEXT.rename}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRenameTeam?.(team.team_id, team.team_name);
                              }}
                            >
                              <Pen className="size-4 shrink-0" />
                            </button>
                            <button
                              type="button"
                              className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full border border-muted-foreground/50 text-muted-foreground/50 transition-colors hover:border-muted-foreground/80 hover:text-muted-foreground/80"
                              aria-label={`${team.team_name} ${TEXT.remove}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteTeam?.(team.team_id);
                              }}
                            >
                              <Trash2Icon className="size-4 shrink-0" />
                            </button>
                          </div>

                          <div
                            className={cn(
                              "mb-0.5 min-w-0 truncate font-pretendard text-sm font-bold",
                              isError ? "text-destructive" : "text-foreground"
                            )}
                            title={team.team_name}
                          >
                            {team.team_name}
                          </div>
                          <div
                            className={cn(
                              "font-pretendard text-[12px] leading-[17px]",
                              isError
                                ? "font-medium text-destructive"
                                : "text-muted-foreground"
                            )}
                          >
                            {teamMemberCount}
                            {TEXT.person}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {shiftDisplayInfo.map((shiftInfo) => (
                  <tr key={shiftInfo.constraintKey}>
                    <td className="min-w-0 break-all  border-border text-center align-middle font-pretendard text-base font-bold leading-tight text-white">
                      <ShiftCode
                        code={shiftInfo.displayId}
                        color={shiftInfo.color}
                      />
                    </td>
                    {displayTeams.map((team) => {
                      const isError = isInvalidTeam(team.team_id);
                      return (
                        <td
                          key={team.team_id}
                          className="border border-border bg-background text-center align-middle transition-colors has-[:focus]:bg-muted/40"
                          onClick={(e) => {
                            const input =
                              e.currentTarget.querySelector("input");
                            input?.focus();
                          }}
                        >
                          <div className="flex py-2 gap-1 items-center justify-center ">
                            <Input
                              // type="number"
                              max={99}
                              value={
                                shiftSettings[team.team_id]?.[
                                  shiftInfo.constraintKey
                                ] ?? 0
                              }
                              onChange={(e) => {
                                const value = e.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                onMinStaffChange(
                                  team.team_id,
                                  shiftInfo.constraintKey,
                                  value
                                );
                              }}
                              className={cn(
                                "w-10 h-8",
                                isError ? "text-destructive" : "text-foreground"
                              )}
                              inputMode="numeric"
                              pattern="[0-9]*"
                            />
                            <span
                              className={cn(
                                "ml-0.5 font-pretendard text-[13px]",
                                isError
                                  ? "text-destructive"
                                  : "text-muted-foreground"
                              )}
                            >
                              {TEXT.person}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CustomModal.Panel>
  );
}
